import { useEffect, useRef, useState, useCallback } from "react";
import Header from "./components/Header";
import AuthScreen from "./components/AuthScreen";
import MapView from "./components/MapView";
import VideoPanel from "./components/VideoPanel";
import StatusTiles from "./components/StatusTiles";
import ControlsPanel from "./components/ControlsPanel";
import NotificationsPanel from "./components/NotificationsPanel";
import VideoRecordingsPanel from "./components/VideoRecordingsPanel";

import useNotifications from "./hooks/useNotifications";
import useTelemetrySimulation from "./hooks/useTelemetrySimulation";
import useDrones from "./hooks/useDrones";
import useWebRTCStream from "./hooks/useWebRTCStream";
import { Trash2 } from "lucide-react";


import "./App.css";
import "./auth/Auth.css";

function App() {
  const [authenticated, setAuthenticated] = useState(!!localStorage.getItem("token"));
  const [isMapPrimary, setIsMapPrimary] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showVideoRecordings, setShowVideoRecordings] = useState(false);
  const [videos, setVideos] = useState([]);

  // Fetch videos on mount
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await fetch('http://localhost:3001/api/videos');
        if (response.ok) {
          const data = await response.json();
          setVideos(data);
        }
      } catch (error) {
        console.error('Failed to fetch videos:', error);
      }
    };
    fetchVideos();
  }, []);

  const handleClipDelete = async (videoId) => {
  if (!window.confirm("Are you sure you want to delete this recording?")) return;

  try {
    const response = await fetch(`http://localhost:3001/api/videos/${videoId}`, {
      method: 'DELETE',
    });

    if (response.ok) {
      // Update the UI immediately by filtering out the deleted video
      setVideos(prev => prev.filter(v => v._id !== videoId));
      alert("Video deleted successfully.");
    } else {
      alert("Failed to delete video from server.");
    }
    } catch (error) {
    console.error("Delete error:", error);
    }
  };

  const token = localStorage.getItem("token");
  const defaultLat = 49.2615;
  const defaultLon = -123.2490;
  const { stream: videoStream, status: videoStatus } = useWebRTCStream(
    process.env.REACT_APP_WS_URL
  );

  // ------------------ DRONES HOOK ------------------
  const {
    drones,
    activeDrone,
    activeDroneId,
    setActiveDroneId,
    addDrone,
    deleteDrone,
    updateDrone,
    setDrones
  } = useDrones(token);

  const [sendWSCommand, setSendWSCommand] = useState(null);
  const [homeLocation, setHomeLocation] = useState(null);

  const handleSendCommandReady = (sendCommand) => {
    setSendWSCommand(() => sendCommand);
  };

  const [realTelemetryReceived, setRealTelemetryReceived] = useState(false);
  const [simulationEnabled, setSimulationEnabled] = useState(true);

  const handleTelemetry = (telemetry) => {
    if (activeDrone) {
      setRealTelemetryReceived(true);
      setSimulationEnabled(false);
      // Map real telemtry dict to this
      const transformedTelemetry = {
        ...activeDrone.telemetry, 
        alt: telemetry.altitude_m,
        yaw: telemetry.imu_yaw_deg,
        lat: telemetry.latitude,
        lon: telemetry.longitude,
        bat_pct: telemetry.battery_charge,
        // Add other mappings as needed
        temperature_c: telemetry.temperature_c,
        pressure_hpa: telemetry.pressure_hpa,
        target_yaw_deg: telemetry.target_yaw_deg,
        imu_pitch_deg: telemetry.imu_pitch_deg,
        imu_roll_deg: telemetry.imu_roll_deg,
        current_draw_ma: telemetry.current_draw_ma,
        power_draw_mw: telemetry.power_draw_mw,
        battery_voltage_mv: telemetry.battery_voltage_mv,
        camera_pos_deg: telemetry.camera_pos_deg,
      };
      updateDrone(activeDrone.id, activeDrone.route, transformedTelemetry);
    }
  };

  const handleHomeLocationUpdate = useCallback((lat, lon) => {
    setHomeLocation({ lat, lon });
  }, []);

  // ------------------ NOTIFICATIONS HOOK ------------------
  const {
    notifications,
    unreadCount,
    pushNotification,
    markAllNotificationsRead
  } = useNotifications();

  const activeDroneRef = useRef(null);
  const lastAlertRef = useRef({ ts: 0, message: "" });
  useEffect(() => {
    activeDroneRef.current = activeDrone;
  }, [activeDrone]);

  const handleAlert = (message) => {
    const drone = activeDroneRef.current;
    if (!drone) return;

    const now = Date.now();
    if (message === lastAlertRef.current.message && (now - lastAlertRef.current.ts) < 3000) {
      return;
    }

    lastAlertRef.current = { ts: now, message };
    pushNotification(drone, message);
  };

  useEffect(() => {
    const wsUrl = process.env.REACT_APP_WS_URL;
    if (!wsUrl) return;

    const ws = new WebSocket(wsUrl);

    ws.onmessage = (event) => {
      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch {
        return;
      }

      if (msg.type === "alert") {
        handleAlert(msg.payload?.message || "Alert received");
      }
    };

    return () => {
      ws.close();
    };
  }, [pushNotification]);

  // ------------------ TELEMETRY SIMULATION ------------------
  useTelemetrySimulation(authenticated, setDrones, defaultLat, defaultLon, simulationEnabled, realTelemetryReceived);

  // ------------------ AUTH SCREEN ------------------
  if (!authenticated) {
    return <AuthScreen setAuthenticated={setAuthenticated} />;
  }

  return (
    <div className="App">
      <Header
        title="Aigribot Dashboard"
        onAddDrone={addDrone}
        activeDroneName={activeDrone?.drone_name}
        activeDroneId={activeDrone?.drone_id}
        unreadCount={unreadCount}
        onBellClick={() => setShowNotifications(!showNotifications)}
        onVideoRecordingsClick={() => setShowVideoRecordings(!showVideoRecordings)}
      />

      {/* ---------------------- MAIN DASHBOARD ---------------------- */}
      {activeDrone ? (
        <div className="drone-view">

          <h2 className="drone-title">
            Drone: {activeDrone.drone_name}
            <span className="drone-id">({activeDrone.drone_id})</span>
          </h2>

          <div className="drone-panels">

            {/* LEFT SIDE */}
            <div className="left-panel">
              <div className="panel-card">
                <div className="panel-label">
                  {isMapPrimary ? "Live Map" : "Video Feed"}
                </div>

                {isMapPrimary ? (
                  <MapView
                    route={activeDrone.route}
                    telemetry={activeDrone.telemetry}
                    token={token}
                    sendWSCommand={sendWSCommand}
                    onHomeLocationUpdate={handleHomeLocationUpdate}
                    setRoute={(route) =>
                      updateDrone(activeDrone.id, route, activeDrone.telemetry)
                    }
                  />
                ) : (
                  <VideoPanel stream={videoStream} status={videoStatus} />
                )}

                {/* PiP */}
                <div
                  className="pip-preview"
                  onClick={() => setIsMapPrimary(!isMapPrimary)}
                >
                  <div className="pip-inner">
                    {isMapPrimary ? (
                      <VideoPanel stream={videoStream} status={videoStatus} isPreview />
                    ) : (
                      <MapView
                        route={activeDrone.route}
                        telemetry={activeDrone.telemetry}
                        token={token}
                        sendWSCommand={sendWSCommand}
                        onHomeLocationUpdate={handleHomeLocationUpdate}
                        isPreview
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT SIDE */}
            <div className="right-panel">
              <div className="panel-card flex-1">
                <div className="panel-label">Operator Controls</div>
                <ControlsPanel
                  wsUrl={process.env.REACT_APP_WS_URL}
                  route={activeDrone.route}
                  setRoute={(route) =>
                    updateDrone(activeDrone.id, route, activeDrone.telemetry)
                  }
                  token={token}
                  onAlert={handleAlert}
                  onSendCommandReady={handleSendCommandReady}
                  onTelemetry={handleTelemetry}
                  homeLocation={homeLocation}
                />
              </div>

              <div className="status-box">
                <StatusTiles
                  telemetry={activeDrone.telemetry}
                  onMove={(dLat, dLon) =>
                    setDrones(prev =>
                      prev.map(d =>
                        d.id === activeDrone.id
                          ? {
                              ...d,
                              telemetry: {
                                ...d.telemetry,
                                lat: d.telemetry.lat + dLat,
                                lon: d.telemetry.lon + dLon
                              }
                            }
                          : d
                      )
                    )
                  }
                  simulationEnabled={simulationEnabled}
                  onToggleSimulation={() => setSimulationEnabled(prev => !prev)}
                  isRealData={realTelemetryReceived}
                  compact
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <p>No drones available. Click "Add Drone" to create one.</p>
      )}

      {/* ---------------------- DRONE FOOTER ---------------------- */}
      {drones.length > 0 && (
        <div className="drone-footer">
          {drones.map(d => (
            <div key={d.id} className="drone-footer-item">
              <button
                className={`header-button ${activeDroneId === d.id ? "active-drone" : ""}`}
                onClick={() => setActiveDroneId(d.id)}
              >
                {d.drone_name}
              </button>

              <button className="delete-button modern-delete" onClick={() => deleteDrone(d.id)}>
                <Trash2 size={16} />
              </button>

            </div>
          ))}
        </div>
      )}

      {/* ---------------------- NOTIFICATIONS PANEL ---------------------- */}
      {showNotifications && (
        <NotificationsPanel
          notifications={notifications}
          onClose={() => setShowNotifications(false)}
          onMarkAllRead={markAllNotificationsRead}
        />
      )}

      {/* ---------------------- VIDEO RECORDINGS PANEL ---------------------- */}
      {showVideoRecordings && (
        <VideoRecordingsPanel
          videos={videos}
          onClose={() => setShowVideoRecordings(false)}
          onDelete={handleClipDelete}
        />
      )}
    </div>
  );
}

export default App;
